import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  MarkerType,
  ConnectionMode,
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './orgchart-overrides.css';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Node Component
const OrgNodeCustom = ({ data, id }) => {
  const { node, isAdmin, onEdit, onDelete, onAddChild, onAddAsset, level, ownerships } = data;
  const isRoot = level === 0;
  // ownerships = [{ label: 'Sacha Kempf', pct: 33, type: 'associate' | 'company' }, ...]
  const owners = ownerships || [];
  const total = owners.reduce((s, o) => s + (o.pct ?? 0), 0);

  return (
    <div className={`min-w-[200px] max-w-[280px] shadow-lg transition-shadow hover:shadow-xl ${
      isRoot ? 'bg-foreground text-white' : 'bg-white border border-border'
    }`} data-testid={`org-node-${id}`}>
      {/* Connection handles */}
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-gold !border-2 !border-white" />
      
      <div className="p-4 text-center">
        {node.photo && (
          <div className="w-12 h-12 rounded-full mx-auto mb-2 overflow-hidden bg-muted">
            <img src={node.photo} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
          </div>
        )}
        <p className="font-heading text-base leading-tight">{node.name}</p>
        {node.title && (
          <p className={`text-[11px] uppercase tracking-wider mt-1 ${isRoot ? 'text-white/70' : 'text-muted-foreground'}`}>
            {node.title}
          </p>
        )}
        {node.description && (
          <p className={`text-[11px] mt-1 ${isRoot ? 'text-white/60' : 'text-muted-foreground'}`}>
            {node.description}
          </p>
        )}
        {/* Ownership breakdown */}
        {owners.length > 0 && (
          <div className={`mt-3 pt-3 border-t ${isRoot ? 'border-white/15' : 'border-border'} space-y-1`}>
            {owners.map((o, i) => (
              <div key={`${o.type}-${o.label}-${i}`} className={`flex items-center justify-between gap-2 text-[11px] ${isRoot ? 'text-white/85' : 'text-foreground/85'}`}>
                <span className="truncate text-left flex-1">{o.label}</span>
                <span className={`font-bold ${o.type === 'associate' ? (isRoot ? 'text-gold' : 'text-gold-dark') : (isRoot ? 'text-white' : 'text-foreground')}`}>
                  {o.pct != null ? `${o.pct}%` : '—'}
                </span>
              </div>
            ))}
            {owners.length > 1 && (
              <div className={`flex items-center justify-between gap-2 pt-1 mt-1 border-t ${isRoot ? 'border-white/10' : 'border-border/60'} text-[10px] uppercase tracking-wider ${isRoot ? 'text-white/60' : 'text-muted-foreground'}`}>
                <span>Total</span>
                <span className="font-bold">{Number.isInteger(total) ? total : total.toFixed(2)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
      
      {isAdmin && (
        <div className={`flex border-t ${isRoot ? 'border-white/20' : 'border-border'}`}>
          <button onClick={() => onAddChild(node)} title="Ajouter une filiale"
            data-testid={`add-filiale-btn-${node.id}`}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] transition-colors ${
              isRoot ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            Filiale
          </button>
          <button onClick={() => onAddAsset?.(node)} title="Ajouter un actif"
            data-testid={`add-asset-btn-${node.id}`}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] border-l transition-colors ${
              isRoot ? 'border-white/20 text-emerald-200 hover:text-white hover:bg-emerald-500/30' : 'border-border text-emerald-600/80 hover:text-emerald-700 hover:bg-emerald-50'
            }`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 21h18M3 7v14M21 7v14M6 21V11l6-4 6 4v10" /></svg>
            Actif
          </button>
          <button onClick={() => onEdit(node)} title="Modifier"
            className={`px-2.5 py-1.5 text-[11px] border-l transition-colors ${
              isRoot ? 'border-white/20 text-white/70 hover:text-white hover:bg-white/10' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button onClick={() => onDelete(node.id)} title="Supprimer"
            className={`px-2.5 py-1.5 text-[11px] border-l transition-colors ${
              isRoot ? 'border-white/20 text-red-300 hover:text-white hover:bg-red-500/30' : 'border-border text-destructive/70 hover:text-destructive hover:bg-destructive/10'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
          </button>
        </div>
      )}
      
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-gold !border-2 !border-white" />
    </div>
  );
};

// Associate Node Component (personnes physiques)
const AssociateNodeCustom = ({ data, id }) => {
  const { associate, isAdmin, onDeleteAssociate, onEditAssociate } = data;
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!associate.photo) return;
    if (associate.photo.includes('/api/files/')) {
      const fileId = associate.photo.split('/api/files/').pop().split('?')[0];
      axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        setImgSrc(`data:${res.data.content_type};base64,${res.data.data}`);
      }).catch(() => {});
    } else {
      setImgSrc(associate.photo);
    }
  }, [associate.photo]);

  return (
    <div className="min-w-[160px] max-w-[200px] bg-white border-2 border-gold/40 shadow-md hover:shadow-lg transition-shadow text-center"
      data-testid={`org-associate-${id}`}>
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-gold !border-2 !border-white" />
      
      <div className="p-3">
        {imgSrc ? (
          <div className="w-14 h-14 rounded-full mx-auto mb-2 overflow-hidden border-2 border-gold/30">
            <img src={imgSrc} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-14 h-14 rounded-full mx-auto mb-2 bg-gold/20 flex items-center justify-center text-gold-dark font-heading text-xl border-2 border-gold/30">
            {associate.name?.charAt(0)}
          </div>
        )}
        <p className="font-heading text-sm leading-tight">{associate.name}</p>
        {associate.position && (
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{associate.position}</p>
        )}
      </div>

      {isAdmin && (
        <div className="border-t border-gold/20 flex">
          <button onClick={() => onEditAssociate(associate)} title="Modifier les participations"
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Modifier
          </button>
          <button onClick={() => onDeleteAssociate(associate.id)} title="Retirer de l'organigramme"
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border-l border-gold/20">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
            Retirer
          </button>
        </div>
      )}
      
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-gold !border-2 !border-white" />
    </div>
  );
};

// Asset Node Component (immobilier)
const AssetNodeCustom = ({ data, id }) => {
  const { asset, onOpen, onDetach, isAdmin } = data;
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const url = asset.main_photo;
    if (!url) return;
    if (url.includes('/api/files/')) {
      const fileId = url.split('/api/files/').pop().split('?')[0];
      axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        setImgSrc(`data:${res.data.content_type};base64,${res.data.data}`);
      }).catch(() => {});
    } else {
      setImgSrc(url);
    }
  }, [asset.main_photo]);

  // Extract city from address (last comma-separated chunk that isn't a postal code)
  const getCity = (address) => {
    if (!address) return '';
    const parts = address.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return '';
    const last = parts[parts.length - 1];
    // Strip postal code pattern (e.g., "75001 Paris" -> "Paris")
    return last.replace(/^\d{4,5}\s*/, '');
  };

  const poleColors = {
    habitation: 'border-emerald-400/50 bg-emerald-50/30',
    hotelerie: 'border-amber-400/50 bg-amber-50/30',
    commercial: 'border-sky-400/50 bg-sky-50/30',
    promotion: 'border-violet-400/50 bg-violet-50/30',
  };
  const poleClass = poleColors[asset.pole] || 'border-slate-400/50 bg-slate-50/30';

  return (
    <div
      className={`group relative min-w-[180px] max-w-[220px] bg-white border-2 ${poleClass} shadow-md hover:shadow-xl hover:scale-[1.02] transition-all overflow-hidden`}
      title={`${asset.name} — ${asset.address || ''}`}
      data-testid={`org-asset-${id}`}>
      {/* Hidden handle positioned 12px inside the card so the arrow ends behind the photo */}
      <Handle
        type="target"
        position={Position.Top}
        className="!border-0 !bg-transparent !opacity-0"
        style={{ top: 12, width: 2, height: 2, pointerEvents: 'none' }}
      />

      {/* Detach button (admin only) */}
      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onDetach?.(asset); }}
          title="Retirer de l'organigramme"
          data-testid={`detach-asset-btn-${asset.id}`}
          className="absolute top-1.5 left-1.5 z-10 w-6 h-6 bg-white/95 backdrop-blur border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all rounded-full shadow-sm">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      )}

      {/* Clickable photo + content */}
      <div onClick={() => onOpen?.(asset)} className="cursor-pointer">
        <div className="relative w-full h-24 bg-muted overflow-hidden">
          {imgSrc ? (
            <img src={imgSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
              <svg className="w-8 h-8 text-muted-foreground/60" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path d="M3 21h18M3 7v14M21 7v14M6 21V11l6-4 6 4v10M9 9v.01M15 9v.01M9 13v.01M15 13v.01M9 17v.01M15 17v.01" />
              </svg>
            </div>
          )}
          <span className="absolute top-1.5 right-1.5 bg-white/95 backdrop-blur text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-semibold text-foreground/80">
            {asset.pole}
          </span>
        </div>

        <div className="p-2.5 text-center">
          <p className="font-heading text-[13px] leading-tight line-clamp-2">{asset.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 truncate">
            {getCity(asset.address) || '—'}
          </p>
        </div>
      </div>
    </div>
  );
};

const TeamMemberNodeCustom = ({ data, id }) => {
  const { member, onDetach, isAdmin } = data;
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const url = member.photo;
    if (!url) return;
    if (url.includes('/api/files/')) {
      const fileId = url.split('/api/files/').pop().split('?')[0];
      axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        setImgSrc(`data:${res.data.content_type};base64,${res.data.data}`);
      }).catch(() => {});
    } else {
      setImgSrc(url);
    }
  }, [member.photo]);

  return (
    <div
      className="group relative min-w-[150px] max-w-[180px] bg-white border-2 border-sky-300/60 bg-sky-50/30 shadow-md hover:shadow-xl hover:scale-[1.02] transition-all overflow-hidden"
      title={`${member.name} — ${member.position || ''}`}
      data-testid={`org-team-${id}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="!border-0 !bg-transparent !opacity-0"
        style={{ top: 12, width: 2, height: 2, pointerEvents: 'none' }}
      />
      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onDetach?.(member); }}
          title="Retirer ce membre de cette société"
          data-testid={`detach-team-btn-${member.id}`}
          className="absolute top-1.5 left-1.5 z-10 w-6 h-6 bg-white/95 backdrop-blur border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all rounded-full shadow-sm">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      )}
      <div className="p-2.5 flex flex-col items-center text-center">
        {imgSrc ? (
          <img src={imgSrc} alt="" className="w-12 h-12 rounded-full object-cover mb-1.5 border-2 border-sky-300/40" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 font-heading text-sm mb-1.5 border-2 border-sky-300/40">
            {(member.name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <p className="font-heading text-[12px] leading-tight line-clamp-2">{member.name}</p>
        {member.position && (
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate w-full">{member.position}</p>
        )}
      </div>
    </div>
  );
};

// Dossier Node Component (acquisition en cours)
const DossierNodeCustom = ({ data, id }) => {
  const { dossier, onOpen, onDetach, isAdmin } = data;
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const url = dossier.main_photo || (dossier.photos || [])[0];
    if (!url) return;
    if (url.includes('/api/files/')) {
      const fileId = url.split('/api/files/').pop().split('?')[0];
      axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        setImgSrc(`data:${res.data.content_type};base64,${res.data.data}`);
      }).catch(() => {});
    } else {
      setImgSrc(url);
    }
  }, [dossier.main_photo, dossier.photos]);

  const statusLabels = { etude: 'Étude', offre: 'Offre', sous_compromis: 'Sous compromis', acte: 'Acte' };

  return (
    <div
      className="group relative min-w-[180px] max-w-[220px] bg-white border-2 border-orange-400/60 bg-orange-50/30 border-dashed shadow-md hover:shadow-xl hover:scale-[1.02] transition-all overflow-hidden"
      title={`${dossier.name} — ${dossier.address || ''}`}
      data-testid={`org-dossier-${id}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="!border-0 !bg-transparent !opacity-0"
        style={{ top: 12, width: 2, height: 2, pointerEvents: 'none' }}
      />
      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onDetach?.(dossier); }}
          title="Retirer de l'organigramme"
          data-testid={`detach-dossier-btn-${dossier.id}`}
          className="absolute top-1.5 left-1.5 z-10 w-6 h-6 bg-white/95 backdrop-blur border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all rounded-full shadow-sm">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      )}
      <div onClick={() => onOpen?.(dossier)} className="cursor-pointer">
        <div className="relative w-full h-24 bg-muted overflow-hidden">
          {imgSrc ? (
            <img src={imgSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100/50">
              <svg className="w-8 h-8 text-orange-400/70" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path d="M3 21h18M3 7v14M21 7v14M6 21V11l6-4 6 4v10" />
              </svg>
            </div>
          )}
          <span className="absolute top-1.5 right-1.5 bg-orange-500 text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-semibold">
            En cours
          </span>
        </div>
        <div className="p-2.5 text-center">
          <p className="font-heading text-[13px] leading-tight line-clamp-2">{dossier.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-orange-700 mt-1 truncate">
            {statusLabels[dossier.status] || dossier.status || '—'}
          </p>
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { orgNode: OrgNodeCustom, associateNode: AssociateNodeCustom, assetNode: AssetNodeCustom, teamMemberNode: TeamMemberNodeCustom, dossierNode: DossierNodeCustom };

// Editable Edge Component with manual waypoints (works for ANY edge in the orgchart)
// - Path goes through waypoints (orthogonal entry/exit, straight L between waypoints)
// - Each waypoint has a draggable handle (small circle)
// - Double-click on the path adds a waypoint at the click position
// - Double-click on a waypoint handle removes it
const EditableEdge = (props) => {
  const { id, sourceX, sourceY, targetX, targetY, markerEnd, style, label, labelStyle, labelBgStyle, labelBgPadding, data } = props;
  const { waypoints = [], onWaypointsChange, isAdmin, handleColor = '#10b981' } = data || {};
  const { screenToFlowPosition } = useReactFlow();
  const dragStateRef = useRef(null);

  const buildPath = () => {
    if (waypoints.length === 0) {
      const midY = (sourceY + targetY) / 2;
      return `M ${sourceX} ${sourceY} L ${sourceX} ${midY} L ${targetX} ${midY} L ${targetX} ${targetY}`;
    }
    const first = waypoints[0];
    const last = waypoints[waypoints.length - 1];
    let d = `M ${sourceX} ${sourceY}`;
    const startMidY = (sourceY + first.y) / 2;
    d += ` L ${sourceX} ${startMidY} L ${first.x} ${startMidY} L ${first.x} ${first.y}`;
    for (let i = 1; i < waypoints.length; i++) {
      d += ` L ${waypoints[i].x} ${waypoints[i].y}`;
    }
    const endMidY = (last.y + targetY) / 2;
    d += ` L ${last.x} ${endMidY} L ${targetX} ${endMidY} L ${targetX} ${targetY}`;
    return d;
  };

  const path = buildPath();

  // Compute label position (midpoint of full path)
  const labelMidX = waypoints.length > 0
    ? waypoints[Math.floor(waypoints.length / 2)].x
    : (sourceX + targetX) / 2;
  const labelMidY = waypoints.length > 0
    ? waypoints[Math.floor(waypoints.length / 2)].y
    : (sourceY + targetY) / 2;

  const handleDoubleClickPath = useCallback((event) => {
    if (!isAdmin) return;
    event.stopPropagation();
    const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    onWaypointsChange?.([...waypoints, { x: flowPos.x, y: flowPos.y }]);
  }, [isAdmin, screenToFlowPosition, waypoints, onWaypointsChange]);

  const handleWaypointMouseDown = useCallback((event, index) => {
    if (!isAdmin) return;
    event.stopPropagation();
    event.preventDefault();
    const startPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    dragStateRef.current = { index, startPos, startWaypoint: waypoints[index] };

    const onMove = (e) => {
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const dx = flowPos.x - dragStateRef.current.startPos.x;
      const dy = flowPos.y - dragStateRef.current.startPos.y;
      const newWaypoints = [...waypoints];
      newWaypoints[dragStateRef.current.index] = {
        x: dragStateRef.current.startWaypoint.x + dx,
        y: dragStateRef.current.startWaypoint.y + dy,
      };
      onWaypointsChange?.(newWaypoints);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragStateRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isAdmin, screenToFlowPosition, waypoints, onWaypointsChange]);

  const handleWaypointDoubleClick = useCallback((event, index) => {
    if (!isAdmin) return;
    event.stopPropagation();
    const newWaypoints = waypoints.filter((_, i) => i !== index);
    onWaypointsChange?.(newWaypoints);
  }, [isAdmin, waypoints, onWaypointsChange]);

  return (
    <>
      {/* Hit area (transparent thicker path for easier double-click) */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onDoubleClick={handleDoubleClickPath}
        style={{ cursor: isAdmin ? 'copy' : 'default', pointerEvents: 'all' }}
      />
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {/* Render label centered on the path */}
      {label != null && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelMidX}px, ${labelMidY}px)`,
              pointerEvents: 'none',
              fontSize: 11,
              fontWeight: labelStyle?.fontWeight ?? 700,
              color: labelStyle?.fill ?? '#8b7355',
              background: labelBgStyle?.fill ?? '#fff',
              border: `1px solid ${labelBgStyle?.stroke ?? '#b8a88a'}`,
              padding: labelBgPadding ? `${labelBgPadding[1]}px ${labelBgPadding[0]}px` : '3px 4px',
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
      {isAdmin && (
        <EdgeLabelRenderer>
          {waypoints.map((wp, i) => (
            <div
              key={i}
              data-testid={`edge-waypoint-${id}-${i}`}
              onMouseDown={(e) => handleWaypointMouseDown(e, i)}
              onDoubleClick={(e) => handleWaypointDoubleClick(e, i)}
              title="Glisser pour déplacer · Double-clic pour supprimer"
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${wp.x}px, ${wp.y}px)`,
                pointerEvents: 'all',
                cursor: 'grab',
                width: 14,
                height: 14,
                background: handleColor,
                border: '2px solid #fff',
                borderRadius: '50%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }}
            />
          ))}
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = { editableEdge: EditableEdge };

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  style: { stroke: '#b8a88a', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#b8a88a', width: 16, height: 16 },
};

export default function OrgChartFlow({ token, isAdmin, onNavigate, organigrammeId }) {
  return (
    <ReactFlowProvider>
      <OrgChartFlowInner token={token} isAdmin={isAdmin} onNavigate={onNavigate} organigrammeId={organigrammeId} />
    </ReactFlowProvider>
  );
}

function OrgChartFlowInner({ token, isAdmin, onNavigate, organigrammeId }) {
  const [orgNodes, setOrgNodes] = useState([]);
  const [orgEdges, setOrgEdges] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [confirmDetachTeamId, setConfirmDetachTeamId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editNode, setEditNode] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showAssociateForm, setShowAssociateForm] = useState(false);
  const [associateMode, setAssociateMode] = useState('existing'); // 'existing' | 'new'
  const [allAssociates, setAllAssociates] = useState([]);
  const [editingAssociate, setEditingAssociate] = useState(null);
  const [confirmDeleteAssocId, setConfirmDeleteAssocId] = useState(null);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [assetMode, setAssetMode] = useState('existing'); // 'existing' | 'new'
  const [confirmDetachAssetId, setConfirmDetachAssetId] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [confirmDetachDossierId, setConfirmDetachDossierId] = useState(null);
  const emptyForm = { name: '', title: '', parent_ids: [], parent_pcts: {}, associate_ids: [], associate_pcts: {}, photo: '', description: '' };
  const emptyAssocForm = { name: '', position: '', company_ids: [], company_pcts: {} };
  const emptyAssetForm = { name: '', address: '', pole: 'habitation', status: 'patrimonial', company_id: '' };
  const [formData, setFormData] = useState(emptyForm);
  const [assocFormData, setAssocFormData] = useState(emptyAssocForm);
  const [assetFormData, setAssetFormData] = useState(emptyAssetForm);
  const saveTimeoutRef = useRef(null);
  const pendingPositionsRef = useRef({});
  const isSavingRef = useRef(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  // Waypoints map for asset edges (legacy, stored on asset.org_edge_waypoints)
  const [assetWaypoints, setAssetWaypoints] = useState({}); // { assetId: [{x,y}, ...] }
  const pendingWaypointsRef = useRef({}); // { assetId: [{x,y}, ...] }
  // Generic edge waypoints (collection edge_waypoints), keyed by edge_id
  const [edgeWaypointsMap, setEdgeWaypointsMap] = useState({}); // { edge_id: [{x,y}, ...] }
  const pendingEdgeWaypointsRef = useRef({}); // { edge_id: [{x,y}, ...] }
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const fetchData = async () => {
    try {
      const params = organigrammeId ? `?organigramme_id=${organigrammeId}` : '';
      const [nodesRes, edgesRes, assocRes, assetsRes, allAssocRes, edgeWpRes, teamsRes, dossiersRes] = await Promise.all([
        api.get(`/orgchart${params}`),
        api.get(`/orgchart-edges${params}`),
        api.get(`/associates${params}`),
        api.get(`/assets`),
        api.get(`/associates`),
        api.get(`/edge-waypoints${params}`),
        api.get(`/teams`),
        api.get(`/dossiers`),
      ]);
      setOrgNodes(nodesRes.data);
      setOrgEdges(edgesRes.data);
      setAssociates(assocRes.data);
      setAssets(assetsRes.data);
      setAllAssociates(allAssocRes.data);
      setTeams(teamsRes.data || []);
      setDossiers(dossiersRes.data || []);
      // Initialize asset waypoints cache (legacy)
      const wpMap = {};
      assetsRes.data.forEach(a => {
        if (a.org_edge_waypoints && a.org_edge_waypoints.length > 0) {
          wpMap[a.id] = a.org_edge_waypoints;
        }
      });
      setAssetWaypoints(wpMap);
      // Initialize generic edge waypoints map
      const ewMap = {};
      edgeWpRes.data.forEach(item => {
        if (item.waypoints && item.waypoints.length > 0) {
          ewMap[item.edge_id] = item.waypoints;
        }
      });
      setEdgeWaypointsMap(ewMap);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Handler called by AssetEdge when waypoints are dragged/added/removed
  const handleEdgeWaypointsChange = useCallback((assetId, newWaypoints) => {
    setAssetWaypoints(prev => ({ ...prev, [assetId]: newWaypoints }));
    pendingWaypointsRef.current[assetId] = newWaypoints;
    setHasUnsavedChanges(true);
    // Update edges in place so the UI re-renders immediately
    setEdges(eds => eds.map(e => {
      if (e.data?.assetId === assetId) {
        return { ...e, data: { ...e.data, waypoints: newWaypoints } };
      }
      return e;
    }));
  }, [setEdges]);

  // Handler for generic edges (parent rel, manual, associate)
  // edge_id is the ReactFlow edge id (e.g. "rel-X-Y", manual edge id, "assoc-edge-X-Y")
  const handleGenericEdgeWaypointsChange = useCallback((edgeId, newWaypoints) => {
    setEdgeWaypointsMap(prev => ({ ...prev, [edgeId]: newWaypoints }));
    pendingEdgeWaypointsRef.current[edgeId] = newWaypoints;
    setHasUnsavedChanges(true);
    setEdges(eds => eds.map(e => {
      if (e.id === edgeId) {
        return { ...e, data: { ...e.data, waypoints: newWaypoints } };
      }
      return e;
    }));
  }, [setEdges]);

  // Convert orgchart data to React Flow format
  useEffect(() => {
    if (orgNodes.length === 0) return;

    // Auto-layout: compute hierarchical positions if not saved
    const computeAutoLayout = () => {
      const nodeMap = {};
      orgNodes.forEach(n => { nodeMap[n.id] = n; });
      
      // Find roots
      const roots = orgNodes.filter(n => {
        const pIds = n.parent_ids?.length > 0 ? n.parent_ids : (n.parent_id ? [n.parent_id] : []);
        return pIds.length === 0;
      });
      
      // BFS to assign levels
      const levels = {};
      const queue = roots.map(r => ({ id: r.id, level: 0 }));
      const visited = new Set();
      while (queue.length > 0) {
        const { id, level } = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);
        levels[id] = level;
        // Find children
        orgNodes.forEach(n => {
          const pIds = n.parent_ids?.length > 0 ? n.parent_ids : (n.parent_id ? [n.parent_id] : []);
          if (pIds.includes(id) && !visited.has(n.id)) {
            queue.push({ id: n.id, level: level + 1 });
          }
        });
      }
      // Nodes not visited (orphans)
      orgNodes.forEach(n => { if (!visited.has(n.id)) levels[n.id] = 0; });
      
      // Group by level
      const byLevel = {};
      Object.entries(levels).forEach(([id, lvl]) => {
        if (!byLevel[lvl]) byLevel[lvl] = [];
        byLevel[lvl].push(id);
      });
      
      // Assign positions
      const positions = {};
      const NODE_W = 280;
      const NODE_H = 200;
      Object.entries(byLevel).forEach(([lvl, ids]) => {
        const totalWidth = ids.length * NODE_W;
        const startX = -totalWidth / 2;
        ids.forEach((id, i) => {
          positions[id] = { x: startX + i * NODE_W, y: parseInt(lvl) * NODE_H };
        });
      });
      return positions;
    };

    const autoPositions = computeAutoLayout();

    // Build ownership map for each company node:
    // - parent companies with their pct (from node.parent_pcts or legacy ownership_pct)
    // - associates that own this company (from associate.company_ids/company_pcts)
    const ownershipsByNode = {};
    orgNodes.forEach(n => {
      const list = [];
      const pIds = n.parent_ids?.length > 0 ? n.parent_ids : (n.parent_id ? [n.parent_id] : []);
      pIds.forEach(pid => {
        const parent = orgNodes.find(p => p.id === pid);
        if (!parent) return;
        let pct = n.parent_pcts?.[pid];
        if (pct == null && pIds.length === 1 && n.ownership_pct != null) pct = n.ownership_pct;
        list.push({ label: parent.name, pct, type: 'company' });
      });
      associates.forEach(a => {
        if ((a.company_ids || []).includes(n.id)) {
          const pct = a.company_pcts?.[n.id];
          list.push({ label: (a.name || '').trim() || '—', pct, type: 'associate' });
        }
      });
      ownershipsByNode[n.id] = list;
    });

    // Build flow nodes with positions
    const flowNodes = orgNodes.map((node) => {
      const parentIds = node.parent_ids?.length > 0 ? node.parent_ids : (node.parent_id ? [node.parent_id] : []);
      const isRoot = parentIds.length === 0;
      const autoPos = autoPositions[node.id] || { x: 0, y: 0 };
      
      return {
        id: node.id,
        type: 'orgNode',
        position: {
          x: node.position_x ?? autoPos.x,
          y: node.position_y ?? autoPos.y,
        },
        data: {
          node,
          isAdmin,
          level: isRoot ? 0 : 1,
          ownerships: ownershipsByNode[node.id] || [],
          onEdit: handleEdit,
          onDelete: (id) => setConfirmDeleteId(id),
          onAddChild: addChild,
          onAddAsset: handleAddAsset,
        },
      };
    });

    // Build flow edges from parent relationships + manual edges
    const flowEdges = [];
    
    // Parent relationship edges (no pct labels — % are shown inside the company card)
    orgNodes.forEach(node => {
      const parentIds = node.parent_ids?.length > 0 ? node.parent_ids : (node.parent_id ? [node.parent_id] : []);
      parentIds.forEach(pid => {
        const edgeId = `rel-${pid}-${node.id}`;
        flowEdges.push({
          id: edgeId,
          source: pid,
          target: node.id,
          type: 'editableEdge',
          animated: false,
          style: { stroke: '#b8a88a', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#b8a88a', width: 16, height: 16 },
          data: {
            isRelationship: true,
            isAdmin,
            handleColor: '#b8a88a',
            waypoints: edgeWaypointsMap[edgeId] || [],
            onWaypointsChange: (wp) => handleGenericEdgeWaypointsChange(edgeId, wp),
          },
        });
      });
    });

    // Manual edges from DB
    orgEdges.forEach(edge => {
      flowEdges.push({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'editableEdge',
        animated: true,
        label: edge.label || undefined,
        labelStyle: { fontSize: 11, fontWeight: 600, fill: '#555' },
        labelBgStyle: { fill: '#fff', stroke: '#ddd', strokeWidth: 1 },
        labelBgPadding: [4, 3],
        style: { stroke: '#666', strokeWidth: 2, strokeDasharray: '6 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#666', width: 16, height: 16 },
        data: {
          isManual: true,
          dbId: edge.id,
          isAdmin,
          handleColor: '#666',
          waypoints: edgeWaypointsMap[edge.id] || [],
          onWaypointsChange: (wp) => handleGenericEdgeWaypointsChange(edge.id, wp),
        },
      });
    });

    // Add associate nodes (positioned above the org tree)
    const ASSOC_NODE_W = 200;
    const assocStartX = -(associates.length * ASSOC_NODE_W) / 2;
    associates.forEach((assoc, idx) => {
      flowNodes.push({
        id: `assoc-${assoc.id}`,
        type: 'associateNode',
        position: {
          x: assoc.position_x ?? (assocStartX + idx * ASSOC_NODE_W),
          y: assoc.position_y ?? -250,
        },
        data: { associate: assoc, isAdmin, onDeleteAssociate: (id) => setConfirmDeleteAssocId(id), onEditAssociate: handleEditAssociate },
      });
      // Edges from associate to their companies (no pct labels — % shown inside company card)
      (assoc.company_ids || []).forEach(companyId => {
        if (orgNodes.find(n => n.id === companyId)) {
          const edgeId = `assoc-edge-${assoc.id}-${companyId}`;
          flowEdges.push({
            id: edgeId,
            source: `assoc-${assoc.id}`,
            target: companyId,
            type: 'editableEdge',
            animated: false,
            style: { stroke: '#c9a96e', strokeWidth: 1.5, strokeDasharray: '4 2' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#c9a96e', width: 12, height: 12 },
            data: {
              isAssociateLink: true,
              isAdmin,
              handleColor: '#c9a96e',
              waypoints: edgeWaypointsMap[edgeId] || [],
              onWaypointsChange: (wp) => handleGenericEdgeWaypointsChange(edgeId, wp),
            },
          });
        }
      });
    });

    // Add asset nodes (linked to their parent companies)
    // Filter assets that belong to a company present in this organigramme
    const orgNodeIds = new Set(orgNodes.map(n => n.id));
    const linkedAssets = assets.filter(a => a.company_id && orgNodeIds.has(a.company_id));
    // Group assets by company for auto-layout
    const assetsByCompany = {};
    linkedAssets.forEach(a => {
      if (!assetsByCompany[a.company_id]) assetsByCompany[a.company_id] = [];
      assetsByCompany[a.company_id].push(a);
    });
    const ASSET_NODE_W = 220;
    const ASSET_GAP = 30;
    const GROUP_GAP = 60;
    // Compute max Y of company nodes to place all assets BELOW the company tree
    const companyYs = orgNodes.map(n => {
      const auto = autoPositions[n.id];
      return n.position_y ?? auto?.y ?? 0;
    });
    const maxCompanyY = companyYs.length > 0 ? Math.max(...companyYs) : 0;
    const ASSETS_BASE_Y = maxCompanyY + 280;

    // Order companies (with assets) by their X position to lay out groups left-to-right
    const companiesWithAssets = Object.keys(assetsByCompany).sort((a, b) => {
      const aX = orgNodes.find(n => n.id === a)?.position_x ?? autoPositions[a]?.x ?? 0;
      const bX = orgNodes.find(n => n.id === b)?.position_x ?? autoPositions[b]?.x ?? 0;
      return aX - bX;
    });

    // Compute total width to center the asset row
    const totalWidth = companiesWithAssets.reduce((sum, cid, i) => {
      const groupW = assetsByCompany[cid].length * (ASSET_NODE_W + ASSET_GAP) - ASSET_GAP;
      return sum + groupW + (i > 0 ? GROUP_GAP : 0);
    }, 0);

    let cursorX = -totalWidth / 2;
    companiesWithAssets.forEach((companyId) => {
      const siblings = assetsByCompany[companyId];
      siblings.forEach((asset) => {
        const autoX = cursorX;
        const autoY = ASSETS_BASE_Y;

        flowNodes.push({
          id: `asset-${asset.id}`,
          type: 'assetNode',
          zIndex: 1000,
          position: {
            x: asset.org_position_x ?? autoX,
            y: asset.org_position_y ?? autoY,
          },
          data: {
            asset,
            isAdmin,
            onOpen: (a) => onNavigate?.(`/assets/${a.id}`),
            onDetach: (a) => setConfirmDetachAssetId(a.id),
          },
        });
        flowEdges.push({
          id: `asset-edge-${asset.company_id}-${asset.id}`,
          source: asset.company_id,
          target: `asset-${asset.id}`,
          type: 'editableEdge',
          zIndex: -1,
          animated: false,
          style: { stroke: '#10b981', strokeWidth: 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981', width: 14, height: 14 },
          data: {
            isAssetLink: true,
            isAdmin,
            assetId: asset.id,
            handleColor: '#10b981',
            waypoints: assetWaypoints[asset.id] ?? asset.org_edge_waypoints ?? [],
            onWaypointsChange: (newWaypoints) => handleEdgeWaypointsChange(asset.id, newWaypoints),
          },
        });
        cursorX += ASSET_NODE_W + ASSET_GAP;
      });
      cursorX += GROUP_GAP - ASSET_GAP; // extra gap between company groups
    });

    // ===== Dossiers en cours attached to companies in this organigramme =====
    const linkedDossiers = dossiers.filter(d => d.company_id && orgNodeIds.has(d.company_id));
    const dossiersByCompany = {};
    linkedDossiers.forEach(d => {
      if (!dossiersByCompany[d.company_id]) dossiersByCompany[d.company_id] = [];
      dossiersByCompany[d.company_id].push(d);
    });
    const companiesWithDossiers = Object.keys(dossiersByCompany).sort((a, b) => {
      const aX = orgNodes.find(n => n.id === a)?.position_x ?? autoPositions[a]?.x ?? 0;
      const bX = orgNodes.find(n => n.id === b)?.position_x ?? autoPositions[b]?.x ?? 0;
      return aX - bX;
    });
    // Continue on the same row as assets, after the last asset group
    companiesWithDossiers.forEach((companyId) => {
      dossiersByCompany[companyId].forEach((dossier) => {
        flowNodes.push({
          id: `dossier-${dossier.id}`,
          type: 'dossierNode',
          zIndex: 1000,
          position: {
            x: dossier.org_position_x ?? cursorX,
            y: dossier.org_position_y ?? ASSETS_BASE_Y,
          },
          data: {
            dossier,
            isAdmin,
            onOpen: (d) => onNavigate?.(`/dossiers/${d.id}`),
            onDetach: (d) => setConfirmDetachDossierId(d.id),
          },
        });
        const edgeId = `dossier-edge-${companyId}-${dossier.id}`;
        flowEdges.push({
          id: edgeId,
          source: companyId,
          target: `dossier-${dossier.id}`,
          type: 'editableEdge',
          zIndex: -1,
          animated: false,
          style: { stroke: '#f59e0b', strokeWidth: 1.5, strokeDasharray: '5 4' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#f59e0b', width: 14, height: 14 },
          data: {
            isDossierLink: true,
            isAdmin,
            dossierId: dossier.id,
            handleColor: '#f59e0b',
            waypoints: edgeWaypointsMap[edgeId] ?? [],
            onWaypointsChange: (newWaypoints) => handleGenericEdgeWaypointsChange(edgeId, newWaypoints),
          },
        });
        cursorX += ASSET_NODE_W + ASSET_GAP;
      });
      cursorX += GROUP_GAP - ASSET_GAP;
    });

    // ===== Team members attached to companies present in this organigramme =====
    const linkedTeams = teams.filter(m => Array.isArray(m.company_ids) && m.company_ids.some(cid => orgNodeIds.has(cid)));
    const teamsByCompany = {};
    linkedTeams.forEach(m => {
      m.company_ids.forEach(cid => {
        if (!orgNodeIds.has(cid)) return;
        if (!teamsByCompany[cid]) teamsByCompany[cid] = [];
        teamsByCompany[cid].push(m);
      });
    });
    const TEAM_NODE_W = 180;
    const TEAM_GAP = 24;
    const TEAM_GROUP_GAP = 60;
    // Place team row BELOW the assets row (or below companies if no assets)
    const TEAMS_BASE_Y = ASSETS_BASE_Y + (linkedAssets.length > 0 || linkedDossiers.length > 0 ? 260 : 0);
    const companiesWithTeams = Object.keys(teamsByCompany).sort((a, b) => {
      const aX = orgNodes.find(n => n.id === a)?.position_x ?? autoPositions[a]?.x ?? 0;
      const bX = orgNodes.find(n => n.id === b)?.position_x ?? autoPositions[b]?.x ?? 0;
      return aX - bX;
    });
    const totalTeamWidth = companiesWithTeams.reduce((sum, cid, i) => {
      const groupW = teamsByCompany[cid].length * (TEAM_NODE_W + TEAM_GAP) - TEAM_GAP;
      return sum + groupW + (i > 0 ? TEAM_GROUP_GAP : 0);
    }, 0);
    let teamCursorX = -totalTeamWidth / 2;
    companiesWithTeams.forEach((companyId) => {
      const siblings = teamsByCompany[companyId];
      siblings.forEach((member) => {
        const autoX = teamCursorX;
        const autoY = TEAMS_BASE_Y;
        flowNodes.push({
          id: `team-${member.id}-${companyId}`,
          type: 'teamMemberNode',
          zIndex: 1000,
          position: {
            x: member.org_position_x ?? autoX,
            y: member.org_position_y ?? autoY,
          },
          data: {
            member,
            isAdmin,
            onDetach: (m) => setConfirmDetachTeamId({ memberId: m.id, companyId }),
          },
        });
        flowEdges.push({
          id: `team-edge-${companyId}-${member.id}`,
          source: companyId,
          target: `team-${member.id}-${companyId}`,
          type: 'editableEdge',
          zIndex: -1,
          animated: false,
          style: { stroke: '#0284c7', strokeWidth: 1.5, strokeDasharray: '4 3' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#0284c7', width: 14, height: 14 },
          data: {
            isTeamLink: true,
            isAdmin,
            teamId: member.id,
            handleColor: '#0284c7',
            waypoints: edgeWaypointsMap[`team-edge-${companyId}-${member.id}`] ?? [],
            onWaypointsChange: (newWaypoints) => handleGenericEdgeWaypointsChange(`team-edge-${companyId}-${member.id}`, newWaypoints),
          },
        });
        teamCursorX += TEAM_NODE_W + TEAM_GAP;
      });
      teamCursorX += TEAM_GROUP_GAP - TEAM_GAP;
    });

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [orgNodes, orgEdges, associates, assets, teams, dossiers, isAdmin]);

  // Flush pending positions to backend
  const flushPositions = useCallback(async () => {
    const allPositions = Object.entries(pendingPositionsRef.current);
    const allWaypoints = Object.entries(pendingWaypointsRef.current);
    const allEdgeWaypoints = Object.entries(pendingEdgeWaypointsRef.current);
    if (allPositions.length === 0 && allWaypoints.length === 0 && allEdgeWaypoints.length === 0) return;
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    const orgPositions = allPositions.filter(([k]) => !k.startsWith('assoc-') && !k.startsWith('asset-') && !k.startsWith('team-') && !k.startsWith('dossier-')).map(([, v]) => v);
    const assocPositions = allPositions.filter(([k]) => k.startsWith('assoc-')).map(([, v]) => v);
    const assetPositions = allPositions.filter(([k]) => k.startsWith('asset-')).map(([, v]) => v);
    const teamPositions = allPositions.filter(([k]) => k.startsWith('team-')).map(([, v]) => v);
    const dossierPositions = allPositions.filter(([k]) => k.startsWith('dossier-')).map(([, v]) => v);
    const waypointsBatch = allWaypoints.map(([id, wp]) => ({ id, waypoints: wp }));
    const genericWaypointsBatch = allEdgeWaypoints.map(([edge_id, wp]) => ({ edge_id, organigramme_id: organigrammeId, waypoints: wp }));
    try {
      const promises = [];
      if (orgPositions.length > 0) promises.push(api.put('/orgchart-positions', orgPositions));
      if (assocPositions.length > 0) promises.push(api.put('/associate-positions', assocPositions));
      if (assetPositions.length > 0) promises.push(api.put('/asset-positions', assetPositions));
      if (teamPositions.length > 0) promises.push(api.put('/team-positions', teamPositions));
      if (dossierPositions.length > 0) promises.push(api.put('/dossier-positions', dossierPositions));
      if (waypointsBatch.length > 0) promises.push(api.put('/asset-edge-waypoints', waypointsBatch));
      if (genericWaypointsBatch.length > 0) promises.push(api.put('/edge-waypoints', genericWaypointsBatch));
      await Promise.all(promises);
      pendingPositionsRef.current = {};
      pendingWaypointsRef.current = {};
      pendingEdgeWaypointsRef.current = {};
    } catch (e) { console.error('Failed to save positions', e); }
    isSavingRef.current = false;
  }, [organigrammeId]);

  // Explicit save button handler
  const handleSavePositions = useCallback(async () => {
    const allPositions = Object.entries(pendingPositionsRef.current);
    const allWaypoints = Object.entries(pendingWaypointsRef.current);
    const allEdgeWaypoints = Object.entries(pendingEdgeWaypointsRef.current);
    if (allPositions.length === 0 && allWaypoints.length === 0 && allEdgeWaypoints.length === 0) {
      toast.success('Rien à enregistrer');
      return;
    }
    setSaving(true);
    const orgPositions = allPositions.filter(([k]) => !k.startsWith('assoc-') && !k.startsWith('asset-') && !k.startsWith('team-') && !k.startsWith('dossier-')).map(([, v]) => v);
    const assocPositions = allPositions.filter(([k]) => k.startsWith('assoc-')).map(([, v]) => v);
    const assetPositions = allPositions.filter(([k]) => k.startsWith('asset-')).map(([, v]) => v);
    const teamPositions = allPositions.filter(([k]) => k.startsWith('team-')).map(([, v]) => v);
    const dossierPositions = allPositions.filter(([k]) => k.startsWith('dossier-')).map(([, v]) => v);
    const waypointsBatch = allWaypoints.map(([id, wp]) => ({ id, waypoints: wp }));
    const genericWaypointsBatch = allEdgeWaypoints.map(([edge_id, wp]) => ({ edge_id, organigramme_id: organigrammeId, waypoints: wp }));
    try {
      const promises = [];
      if (orgPositions.length > 0) promises.push(api.put('/orgchart-positions', orgPositions));
      if (assocPositions.length > 0) promises.push(api.put('/associate-positions', assocPositions));
      if (assetPositions.length > 0) promises.push(api.put('/asset-positions', assetPositions));
      if (teamPositions.length > 0) promises.push(api.put('/team-positions', teamPositions));
      if (dossierPositions.length > 0) promises.push(api.put('/dossier-positions', dossierPositions));
      if (waypointsBatch.length > 0) promises.push(api.put('/asset-edge-waypoints', waypointsBatch));
      if (genericWaypointsBatch.length > 0) promises.push(api.put('/edge-waypoints', genericWaypointsBatch));
      await Promise.all(promises);
      pendingPositionsRef.current = {};
      pendingWaypointsRef.current = {};
      pendingEdgeWaypointsRef.current = {};
      setHasUnsavedChanges(false);
      toast.success('Positions enregistrées');
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
    setSaving(false);
  }, [organigrammeId]);

  // Save positions when nodes are dragged
  const onNodeDragStop = useCallback((event, node) => {
    // For prefixed nodes (associate/asset/team), store with real ID
    let realId = node.id;
    if (node.id.startsWith('assoc-')) realId = node.id.replace('assoc-', '');
    else if (node.id.startsWith('asset-')) realId = node.id.replace('asset-', '');
    else if (node.id.startsWith('dossier-')) realId = node.id.replace('dossier-', '');
    else if (node.id.startsWith('team-')) {
      // team-${memberId}-${companyId} → extract memberId
      const rest = node.id.slice('team-'.length);
      // memberId is everything up to the last "-uuid" (uuid has 36 chars w/ dashes); split safely
      const idx = rest.length - 36 - 1; // 36-char companyId + 1 dash
      realId = idx > 0 ? rest.slice(0, idx) : rest;
    }
    pendingPositionsRef.current[node.id] = {
      id: realId,
      position_x: node.position.x,
      position_y: node.position.y,
    };
    setHasUnsavedChanges(true);
  }, []);

  // Save on unmount (leaving page) and on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const positions = Object.values(pendingPositionsRef.current);
      if (positions.length > 0) {
        // Use sendBeacon for reliable save on page exit
        const blob = new Blob([JSON.stringify(positions)], { type: 'application/json' });
        navigator.sendBeacon(`${API}/orgchart-positions-beacon?token=${token}`, blob);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Flush on component unmount (navigation)
      flushPositions();
    };
  }, [flushPositions, token]);

  // Handle new connection (manual link)
  const onConnect = useCallback(async (params) => {
    if (!isAdmin) return;
    try {
      const res = await api.post('/orgchart-edges', {
        source: params.source,
        target: params.target,
        organigramme_id: organigrammeId || null,
      });
      setOrgEdges(prev => [...prev, res.data]);
      toast.success('Lien créé');
    } catch (e) { toast.error('Erreur lors de la création du lien'); }
  }, [isAdmin, organigrammeId]);

  // Handle edge click (delete manual edges)
  const onEdgeClick = useCallback((event, edge) => {
    if (!isAdmin) return;
    if (edge.data?.isManual) {
      setEdges(eds => eds.filter(e => e.id !== edge.id));
      // Delete from DB
      api.delete(`/orgchart-edges/${edge.data.dbId}`).then(() => {
        setOrgEdges(prev => prev.filter(e => e.id !== edge.data.dbId));
        toast.success('Lien supprimé');
      }).catch(() => toast.error('Erreur'));
    }
  }, [isAdmin]);

  const handleEdit = useCallback((node) => {
    setEditNode(node);
    // Build parent_ids from either parent_ids array or legacy parent_id
    const parentIds = (node.parent_ids && node.parent_ids.length > 0)
      ? node.parent_ids
      : (node.parent_id ? [node.parent_id] : []);
    // Build parent_pcts as { parent_id: stringPct }
    const parentPcts = {};
    if (node.parent_pcts) {
      Object.entries(node.parent_pcts).forEach(([k, v]) => { parentPcts[k] = String(v); });
    } else if (node.ownership_pct != null && parentIds.length === 1) {
      // Legacy: single parent with ownership_pct on the node itself
      parentPcts[parentIds[0]] = String(node.ownership_pct);
    }
    // Pre-fill associates (find those whose company_ids contain this node)
    const associateIds = [];
    const associatePcts = {};
    associates.forEach(a => {
      if ((a.company_ids || []).includes(node.id)) {
        associateIds.push(a.id);
        const v = a.company_pcts?.[node.id];
        associatePcts[a.id] = v != null ? String(v) : '';
      }
    });
    setFormData({
      name: node.name,
      title: node.title || '',
      parent_ids: parentIds,
      parent_pcts: parentPcts,
      associate_ids: associateIds,
      associate_pcts: associatePcts,
      photo: node.photo || '',
      description: node.description || '',
    });
    setShowForm(true);
  }, [associates]);

  const addChild = useCallback((parentNode) => {
    setEditNode(null);
    setFormData({ ...emptyForm, parent_ids: [parentNode.id], parent_pcts: { [parentNode.id]: '' } });
    setShowForm(true);
  }, []);

  const handleAddAsset = useCallback((parentNode) => {
    setAssetFormData({ ...emptyAssetForm, company_id: parentNode.id });
    // Default mode: existing if there are unattached assets, otherwise new
    setAssetMode('existing');
    setShowAssetForm(true);
  }, []);

  const handleAttachExistingAsset = async (assetId, companyId) => {
    try {
      await api.put(`/assets/${assetId}`, { company_id: companyId });
      toast.success('Actif rattaché');
      setShowAssetForm(false);
      setAssetFormData(emptyAssetForm);
      fetchData();
    } catch (e) { toast.error("Erreur lors du rattachement"); }
  };

  const handleAttachExistingDossier = async (dossierId, companyId) => {
    try {
      await api.put(`/dossiers/${dossierId}`, { company_id: companyId });
      toast.success('Dossier rattaché');
      setShowAssetForm(false);
      setAssetFormData(emptyAssetForm);
      fetchData();
    } catch (e) { toast.error("Erreur lors du rattachement"); }
  };

  const handleDetachDossier = async (dossierId) => {
    try {
      await api.put(`/dossiers/${dossierId}`, { company_id: "" });
      toast.success("Dossier retiré de l'organigramme");
      setConfirmDetachDossierId(null);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      name: assetFormData.name,
      address: assetFormData.address,
      pole: assetFormData.pole,
      status: assetFormData.status,
      company_id: assetFormData.company_id,
      surface: 0,
      acquisition_date: today,
      acquisition_price: 0,
      current_valuation: 0,
      rent_type: 'annuel',
      annual_rent: 0,
      seasonal_revenue: 0,
      debt_amount: 0,
      ownership_percentage: 100,
    };
    try {
      await api.post('/assets', payload);
      toast.success('Actif ajouté');
      setShowAssetForm(false);
      setAssetFormData(emptyAssetForm);
      fetchData();
    } catch (e) { toast.error("Erreur lors de l'ajout"); }
  };

  const handleDetachAsset = async (assetId) => {
    try {
      await api.put(`/assets/${assetId}`, { company_id: '' });
      toast.success("Actif retiré de l'organigramme");
      setConfirmDetachAssetId(null);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Convert parent_pcts to numbers (only for selected parents)
    const numericPcts = {};
    formData.parent_ids.forEach(pid => {
      const v = formData.parent_pcts[pid];
      if (v !== undefined && v !== '') {
        const n = parseFloat(v);
        if (!isNaN(n)) numericPcts[pid] = n;
      }
    });
    const payload = {
      name: formData.name,
      title: formData.title,
      photo: formData.photo,
      description: formData.description,
      parent_id: formData.parent_ids[0] || null, // legacy
      parent_ids: formData.parent_ids,
      parent_pcts: numericPcts,
      // Legacy ownership_pct = pct of first parent (for backward compat with existing UI badges)
      ownership_pct: formData.parent_ids[0] ? (numericPcts[formData.parent_ids[0]] ?? null) : null,
      organigramme_id: organigrammeId || null,
    };
    try {
      let companyId;
      if (editNode) {
        await api.put(`/orgchart/${editNode.id}`, payload);
        companyId = editNode.id;
        toast.success('Société modifiée');
      } else {
        const res = await api.post('/orgchart', payload);
        companyId = res.data?.id;
        toast.success('Société ajoutée');
      }

      // Sync associates: update each associate's company_ids/company_pcts to reflect
      // the selection made in this company's form.
      if (companyId) {
        const newAssocIds = new Set(formData.associate_ids);
        const updates = [];
        // For every known associate in this orgchart, check if its link to this company changed
        associates.forEach(a => {
          const wasLinked = (a.company_ids || []).includes(companyId);
          const willBeLinked = newAssocIds.has(a.id);
          const newPctRaw = formData.associate_pcts[a.id];
          const newPctNum = (newPctRaw !== undefined && newPctRaw !== '' && !isNaN(parseFloat(newPctRaw)))
            ? parseFloat(newPctRaw) : null;
          const oldPct = a.company_pcts?.[companyId];

          if (willBeLinked && !wasLinked) {
            // Add link
            const newCompanyIds = [...(a.company_ids || []), companyId];
            const newCompanyPcts = { ...(a.company_pcts || {}) };
            if (newPctNum != null) newCompanyPcts[companyId] = newPctNum;
            updates.push(api.put(`/associates/${a.id}`, {
              company_ids: newCompanyIds,
              company_pcts: newCompanyPcts,
            }));
          } else if (!willBeLinked && wasLinked) {
            // Remove link
            const newCompanyIds = (a.company_ids || []).filter(c => c !== companyId);
            const newCompanyPcts = { ...(a.company_pcts || {}) };
            delete newCompanyPcts[companyId];
            updates.push(api.put(`/associates/${a.id}`, {
              company_ids: newCompanyIds,
              company_pcts: newCompanyPcts,
            }));
          } else if (willBeLinked && wasLinked && newPctNum !== oldPct) {
            // Update pct only
            const newCompanyPcts = { ...(a.company_pcts || {}) };
            if (newPctNum != null) newCompanyPcts[companyId] = newPctNum;
            else delete newCompanyPcts[companyId];
            updates.push(api.put(`/associates/${a.id}`, {
              company_pcts: newCompanyPcts,
            }));
          }
        });
        if (updates.length > 0) await Promise.all(updates);
      }

      setShowForm(false);
      setEditNode(null);
      setFormData(emptyForm);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/orgchart/${id}`);
      toast.success('Société supprimée');
      setConfirmDeleteId(null);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleAssociateSubmit = async (e) => {
    e.preventDefault();
    // Convert pcts strings to numbers
    const cleanPcts = {};
    Object.entries(assocFormData.company_pcts).forEach(([k, v]) => {
      if (v !== '' && v != null) cleanPcts[k] = parseFloat(v);
    });
    try {
      if (editingAssociate) {
        await api.put(`/associates/${editingAssociate.id}`, {
          name: assocFormData.name,
          position: assocFormData.position,
          company_ids: assocFormData.company_ids,
          company_pcts: cleanPcts,
        });
        toast.success('Associé modifié');
      } else {
        await api.post('/associates', {
          name: assocFormData.name,
          position: assocFormData.position,
          company_ids: assocFormData.company_ids,
          company_pcts: cleanPcts,
          organigramme_ids: organigrammeId ? [organigrammeId] : [],
        });
        toast.success('Associé ajouté');
      }
      setShowAssociateForm(false);
      setEditingAssociate(null);
      setAssocFormData(emptyAssocForm);
      fetchData();
    } catch (e) {
      console.error('Associate submit error:', e?.response?.data || e);
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(typeof msg === 'string' ? msg : "Erreur lors de l'enregistrement");
    }
  };

  const handleEditAssociate = useCallback((assoc) => {
    setEditingAssociate(assoc);
    const pcts = {};
    Object.entries(assoc.company_pcts || {}).forEach(([k, v]) => { pcts[k] = String(v); });
    setAssocFormData({
      name: assoc.name || '',
      position: assoc.position || '',
      company_ids: assoc.company_ids || [],
      company_pcts: pcts,
    });
    setAssociateMode('new'); // Edit mode shows the full form
    setShowAssociateForm(true);
  }, []);

  const handleAttachExistingAssociate = async (assocId) => {
    try {
      // Append current organigramme to the associate's list (don't replace)
      const existing = allAssociates.find(a => a.id === assocId);
      const currentList = Array.isArray(existing?.organigramme_ids) ? existing.organigramme_ids : (existing?.organigramme_id ? [existing.organigramme_id] : []);
      const nextList = currentList.includes(organigrammeId) ? currentList : [...currentList, organigrammeId];
      await api.put(`/associates/${assocId}`, { organigramme_ids: nextList });
      toast.success('Associé rattaché');
      setShowAssociateForm(false);
      setAssocFormData(emptyAssocForm);
      fetchData();
    } catch (e) { console.error(e); toast.error("Erreur lors du rattachement"); }
  };

  const handleDeleteAssociate = async (id) => {
    try {
      // Detach only from THIS organigramme (keep in others).
      const existing = associates.find(a => a.id === id) || allAssociates.find(a => a.id === id);
      const currentList = Array.isArray(existing?.organigramme_ids) ? existing.organigramme_ids : (existing?.organigramme_id ? [existing.organigramme_id] : []);
      const nextList = currentList.filter(oid => oid !== organigrammeId);
      await api.put(`/associates/${id}`, { organigramme_ids: nextList });
      toast.success("Associé retiré de l'organigramme");
      setConfirmDeleteAssocId(null);
      fetchData();
    } catch (e) { console.error(e); toast.error("Erreur"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="orgchart-page">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => onNavigate('/orgchart')} 
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-to-organigrammes-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
          <div>
            <p className="label-uppercase mb-1">Organigramme</p>
            <h1 className="font-heading text-3xl">Organigramme des sociétés</h1>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-3">
            <button onClick={handleSavePositions} disabled={saving || !hasUnsavedChanges}
              className={`inline-flex items-center gap-2 px-5 py-3 uppercase tracking-widest text-sm transition-colors ${
                hasUnsavedChanges 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'border border-border text-muted-foreground'
              } disabled:opacity-50`}
              data-testid="save-positions-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
              {saving ? 'Enregistrement...' : hasUnsavedChanges ? 'Enregistrer' : 'Enregistré'}
            </button>
            <button onClick={() => { setEditingAssociate(null); setAssocFormData(emptyAssocForm); setAssociateMode('existing'); setShowAssociateForm(true); }}
              className="inline-flex items-center gap-2 border border-gold text-gold-dark px-5 py-3 uppercase tracking-widest text-sm hover:bg-gold/10 transition-colors"
              data-testid="add-associate-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M12 7a4 4 0 100-8 4 4 0 000 8M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
              Ajouter un associé
            </button>
            <button onClick={() => { setEditNode(null); setFormData(emptyForm); setShowForm(true); }}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
              data-testid="add-org-node-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
              Ajouter une société
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      {isAdmin && (
        <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground bg-muted/50 border border-border px-4 py-2" data-testid="orgchart-instructions">
          <span><strong>Molette</strong> = Zoom</span>
          <span><strong>Glisser un noeud</strong> = Déplacer</span>
          <span><strong>Glisser depuis un point</strong> = Créer un lien</span>
          <span><strong>Clic sur un lien pointillé</strong> = Supprimer le lien</span>
          <span><strong>Clic sur un actif</strong> = Voir la fiche détaillée</span>
          <span><strong>Double-clic sur un lien</strong> = Ajouter un point · <strong>Glisser un point</strong> = Déplacer · <strong>Double-clic sur un point</strong> = Supprimer</span>
        </div>
      )}

      {/* React Flow Canvas */}
      <div className="h-[calc(100vh-220px)] border border-border bg-white" data-testid="orgchart-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionMode={ConnectionMode.Loose}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.1}
          maxZoom={3}
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
        >
          <Controls position="bottom-right" showInteractive={false} />
          <Background color="#f0ede8" gap={24} size={1} />
        </ReactFlow>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-2xl mb-6">{editNode ? 'Modifier' : 'Ajouter'} une société</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-uppercase block mb-1">Nom de la société</label>
                <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required
                  placeholder="Ex: SEEK Capital, SCI Les Oliviers..."
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="org-input-name" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Type / Forme juridique</label>
                <input value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))}
                  placeholder="Ex: Holding, SCI, SAS, SARL..."
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="org-input-title" />
              </div>
              <div>
                <label className="label-uppercase block mb-2">Société(s) mère(s)</label>
                <p className="text-xs text-muted-foreground mb-2">Cochez les sociétés propriétaires et indiquez le % de détention de chacune. Aucune coche = société racine.</p>
                <div className="space-y-2 max-h-56 overflow-y-auto border border-border p-3" data-testid="org-parents-list">
                  {orgNodes.filter(n => n.id !== editNode?.id).map(n => {
                    const isSelected = formData.parent_ids.includes(n.id);
                    return (
                      <div key={n.id} className="flex items-center gap-2">
                        <input type="checkbox" checked={isSelected}
                          data-testid={`org-parent-checkbox-${n.id}`}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(p => ({
                                ...p,
                                parent_ids: [...p.parent_ids, n.id],
                                parent_pcts: { ...p.parent_pcts, [n.id]: '' },
                              }));
                            } else {
                              setFormData(p => {
                                const newPcts = { ...p.parent_pcts };
                                delete newPcts[n.id];
                                return {
                                  ...p,
                                  parent_ids: p.parent_ids.filter(id => id !== n.id),
                                  parent_pcts: newPcts,
                                };
                              });
                            }
                          }}
                          className="w-4 h-4 accent-gold" />
                        <span className="flex-1 text-sm">{n.name}{n.title ? ` (${n.title})` : ''}</span>
                        {isSelected && (
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0" max="100"
                              value={formData.parent_pcts[n.id] || ''}
                              data-testid={`org-parent-pct-${n.id}`}
                              onChange={e => setFormData(p => ({...p, parent_pcts: {...p.parent_pcts, [n.id]: e.target.value}}))}
                              placeholder="%" className="w-16 border border-input px-2 py-1 text-sm text-center focus:border-primary focus:outline-none" />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {orgNodes.filter(n => n.id !== editNode?.id).length === 0 && (
                    <p className="text-sm text-muted-foreground">Aucune autre société dans cet organigramme</p>
                  )}
                </div>
              </div>
              <div>
                <label className="label-uppercase block mb-2">Associé(s) propriétaire(s)</label>
                <p className="text-xs text-muted-foreground mb-2">Cochez les associés qui détiennent cette société et indiquez leur % de détention.</p>
                <div className="space-y-2 max-h-56 overflow-y-auto border border-border p-3" data-testid="org-associates-list">
                  {associates.map(a => {
                    const isSelected = formData.associate_ids.includes(a.id);
                    return (
                      <div key={a.id} className="flex items-center gap-2">
                        <input type="checkbox" checked={isSelected}
                          data-testid={`org-assoc-checkbox-${a.id}`}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(p => ({
                                ...p,
                                associate_ids: [...p.associate_ids, a.id],
                                associate_pcts: { ...p.associate_pcts, [a.id]: '' },
                              }));
                            } else {
                              setFormData(p => {
                                const newPcts = { ...p.associate_pcts };
                                delete newPcts[a.id];
                                return {
                                  ...p,
                                  associate_ids: p.associate_ids.filter(id => id !== a.id),
                                  associate_pcts: newPcts,
                                };
                              });
                            }
                          }}
                          className="w-4 h-4 accent-gold" />
                        <span className="flex-1 text-sm">{a.name}{a.position ? ` — ${a.position}` : ''}</span>
                        {isSelected && (
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0" max="100"
                              value={formData.associate_pcts[a.id] || ''}
                              data-testid={`org-assoc-pct-${a.id}`}
                              onChange={e => setFormData(p => ({...p, associate_pcts: {...p.associate_pcts, [a.id]: e.target.value}}))}
                              placeholder="%" className="w-16 border border-input px-2 py-1 text-sm text-center focus:border-primary focus:outline-none" />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {associates.length === 0 && (
                    <p className="text-sm text-muted-foreground">Aucun associé dans cet organigramme. Ajoutez-en via le bouton "Ajouter un associé".</p>
                  )}
                </div>
              </div>
              <div>
                <label className="label-uppercase block mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))}
                  placeholder="Objet social, activité..."
                  className="w-full border border-input px-3 py-2 min-h-16 resize-none focus:border-primary focus:outline-none" data-testid="org-input-description" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90" data-testid="org-submit-btn">
                  {editNode ? 'Modifier' : 'Ajouter'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Supprimer cette société ?</h2>
            <p className="text-sm text-muted-foreground mb-4">Cette action est irréversible. Les filiales seront détachées.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDeleteId)}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-delete-btn">Supprimer</button>
              <button onClick={() => setConfirmDeleteId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted"
                data-testid="cancel-delete-btn">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Associate Form Modal */}
      {showAssociateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAssociateForm(false)}>
          <div className="bg-white p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-associate-modal">
            <h2 className="font-heading text-2xl mb-2">{editingAssociate ? 'Modifier' : 'Ajouter'} un associé</h2>
            {!editingAssociate && (
              <p className="text-xs text-muted-foreground mb-4">
                Rattaché à : <strong>{orgNodes.length > 0 ? 'cet organigramme' : '—'}</strong>
              </p>
            )}

            {/* Tabs (only for add mode) */}
            {!editingAssociate && (
              <div className="flex border-b border-border mb-5">
                <button type="button" onClick={() => setAssociateMode('existing')}
                  data-testid="assoc-tab-existing"
                  className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                    associateMode === 'existing' ? 'border-b-2 border-primary text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  Sélectionner un associé existant
                </button>
                <button type="button" onClick={() => setAssociateMode('new')}
                  data-testid="assoc-tab-new"
                  className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                    associateMode === 'new' ? 'border-b-2 border-primary text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  Créer un nouvel associé
                </button>
              </div>
            )}

            {!editingAssociate && associateMode === 'existing' ? (
              <div data-testid="assoc-existing-list" className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {(() => {
                  const candidates = allAssociates.filter(a => {
                    const ids = Array.isArray(a.organigramme_ids) ? a.organigramme_ids : (a.organigramme_id ? [a.organigramme_id] : []);
                    return !ids.includes(organigrammeId);
                  });
                  if (candidates.length === 0) {
                    return (
                      <div className="text-center py-8 text-sm text-muted-foreground">
                        <p>Aucun associé disponible à rattacher.</p>
                        <button type="button" onClick={() => setAssociateMode('new')}
                          className="mt-3 text-primary underline text-xs">Créer un nouvel associé</button>
                      </div>
                    );
                  }
                  return candidates.map(assoc => (
                    <button type="button" key={assoc.id}
                      onClick={() => handleAttachExistingAssociate(assoc.id)}
                      data-testid={`select-existing-assoc-${assoc.id}`}
                      className="group w-full flex items-center gap-3 p-2.5 border border-border hover:border-primary hover:bg-muted/40 transition-colors text-left">
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex-shrink-0 flex items-center justify-center text-gold-dark font-heading text-base border border-gold/30">
                        {assoc.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-sm truncate">{assoc.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{assoc.position || '—'}</p>
                        {(() => {
                          const ids = Array.isArray(assoc.organigramme_ids) ? assoc.organigramme_ids : (assoc.organigramme_id ? [assoc.organigramme_id] : []);
                          return ids.length > 0 ? (
                            <span className="text-[10px] text-amber-700 italic">Déjà dans {ids.length} organigramme{ids.length > 1 ? 's' : ''}</span>
                          ) : (
                            <span className="text-[10px] text-emerald-700 italic">Non rattaché</span>
                          );
                        })()}
                      </div>
                      <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ));
                })()}
                <div className="pt-3 flex justify-end">
                  <button type="button" onClick={() => setShowAssociateForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAssociateSubmit} className="space-y-4">
                <div>
                  <label className="label-uppercase block mb-1">Nom</label>
                  <input value={assocFormData.name} onChange={e => setAssocFormData(p => ({...p, name: e.target.value}))} required
                    placeholder="Prénom Nom"
                    className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="assoc-input-name" />
                </div>
                <div>
                  <label className="label-uppercase block mb-1">Fonction / Poste</label>
                  <input value={assocFormData.position} onChange={e => setAssocFormData(p => ({...p, position: e.target.value}))} required
                    placeholder="Ex: Gérant, Directeur, Associé..."
                    className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="assoc-input-position" />
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Participations dans les sociétés</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-border p-3">
                    {orgNodes.map(node => {
                      const isSelected = assocFormData.company_ids.includes(node.id);
                      return (
                        <div key={node.id} className="flex items-center gap-2">
                          <input type="checkbox" checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAssocFormData(p => ({...p, company_ids: [...p.company_ids, node.id], company_pcts: {...p.company_pcts, [node.id]: ''}}));
                              } else {
                                setAssocFormData(p => {
                                  const newPcts = {...p.company_pcts};
                                  delete newPcts[node.id];
                                  return {...p, company_ids: p.company_ids.filter(id => id !== node.id), company_pcts: newPcts};
                                });
                              }
                            }}
                            className="w-4 h-4 accent-gold" />
                          <span className="flex-1 text-sm">{node.name}{node.title ? ` (${node.title})` : ''}</span>
                          {isSelected && (
                            <div className="flex items-center gap-1">
                              <input type="number" step="0.01" min="0" max="100"
                                value={assocFormData.company_pcts[node.id] || ''}
                                onChange={e => setAssocFormData(p => ({...p, company_pcts: {...p.company_pcts, [node.id]: e.target.value}}))}
                                placeholder="%" className="w-16 border border-input px-2 py-1 text-sm text-center focus:border-primary focus:outline-none" />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {orgNodes.length === 0 && <p className="text-sm text-muted-foreground">Aucune société dans cet organigramme</p>}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90"
                    data-testid="assoc-submit-btn">{editingAssociate ? 'Modifier' : 'Ajouter'}</button>
                  <button type="button" onClick={() => { setShowAssociateForm(false); setEditingAssociate(null); }} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Delete associate confirmation */}
      {confirmDeleteAssocId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteAssocId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="delete-assoc-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Retirer cet associé ?</h2>
            <p className="text-sm text-muted-foreground mb-4">L'associé sera retiré de cet organigramme mais restera disponible dans la page Associés.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDeleteAssociate(confirmDeleteAssocId)}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-delete-assoc-btn">Retirer</button>
              <button onClick={() => setConfirmDeleteAssocId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Add asset modal */}
      {showAssetForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAssetForm(false)}>
          <div className="bg-white p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-asset-modal">
            <h2 className="font-heading text-2xl mb-2">Ajouter un actif</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Rattaché à : <strong>{orgNodes.find(n => n.id === assetFormData.company_id)?.name || '—'}</strong>
            </p>

            {/* Tabs */}
            <div className="flex border-b border-border mb-5">
              <button type="button" onClick={() => setAssetMode('existing')}
                data-testid="asset-tab-existing"
                className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                  assetMode === 'existing' ? 'border-b-2 border-primary text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}>
                Sélectionner un actif existant
              </button>
              <button type="button" onClick={() => setAssetMode('new')}
                data-testid="asset-tab-new"
                className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                  assetMode === 'new' ? 'border-b-2 border-primary text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}>
                Créer un nouvel actif
              </button>
            </div>

            {assetMode === 'existing' ? (
              <div data-testid="asset-existing-list" className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {(() => {
                  const candidates = assets.filter(a => a.company_id !== assetFormData.company_id);
                  const dossierCandidates = dossiers.filter(d => d.company_id !== assetFormData.company_id);
                  if (candidates.length === 0 && dossierCandidates.length === 0) {
                    return (
                      <div className="text-center py-8 text-sm text-muted-foreground">
                        <p>Aucun actif disponible à rattacher.</p>
                        <button type="button" onClick={() => setAssetMode('new')}
                          className="mt-3 text-primary underline text-xs">Créer un nouvel actif</button>
                      </div>
                    );
                  }
                  return (
                    <>
                      {candidates.map(asset => {
                    const currentCompany = orgNodes.find(n => n.id === asset.company_id);
                    return (
                      <button type="button" key={asset.id}
                        onClick={() => handleAttachExistingAsset(asset.id, assetFormData.company_id)}
                        data-testid={`select-existing-asset-${asset.id}`}
                        className="group w-full flex items-center gap-3 p-2.5 border border-border hover:border-primary hover:bg-muted/40 transition-colors text-left">
                        <div className="w-12 h-12 bg-muted overflow-hidden flex-shrink-0">
                          {asset.main_photo ? (
                            <img src={asset.main_photo.includes('/api/files/') ? asset.main_photo : asset.main_photo}
                              alt="" className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display='none'} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-muted-foreground/60" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path d="M3 21h18M3 7v14M21 7v14M6 21V11l6-4 6 4v10" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-sm truncate">{asset.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{asset.address || '—'}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-muted text-foreground/70 font-semibold">
                              {asset.pole}
                            </span>
                            {currentCompany && (
                              <span className="text-[10px] text-amber-700 italic truncate">
                                Actuellement : {currentCompany.name}
                              </span>
                            )}
                            {!asset.company_id && (
                              <span className="text-[10px] text-emerald-700 italic">Non rattaché</span>
                            )}
                          </div>
                        </div>
                        <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                      </button>
                    );
                  })}
                      {dossierCandidates.length > 0 && (
                        <p className="pt-3 pb-1 text-[10px] uppercase tracking-widest text-orange-700 font-semibold">Dossiers en cours</p>
                      )}
                      {dossierCandidates.map(dossier => {
                        const currentCompany = orgNodes.find(n => n.id === dossier.company_id);
                        return (
                          <button type="button" key={dossier.id}
                            onClick={() => handleAttachExistingDossier(dossier.id, assetFormData.company_id)}
                            data-testid={`select-existing-dossier-${dossier.id}`}
                            className="group w-full flex items-center gap-3 p-2.5 border border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50/40 transition-colors text-left">
                            <div className="w-12 h-12 bg-orange-50 overflow-hidden flex-shrink-0 flex items-center justify-center">
                              <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path d="M3 21h18M3 7v14M21 7v14M6 21V11l6-4 6 4v10" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-heading text-sm truncate">{dossier.name}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{dossier.address || '—'}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-orange-500 text-white font-semibold">
                                  En cours
                                </span>
                                {currentCompany && (
                                  <span className="text-[10px] text-amber-700 italic truncate">
                                    Actuellement : {currentCompany.name}
                                  </span>
                                )}
                                {!dossier.company_id && (
                                  <span className="text-[10px] text-emerald-700 italic">Non rattaché</span>
                                )}
                              </div>
                            </div>
                            <svg className="w-4 h-4 text-muted-foreground group-hover:text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
                <div className="pt-3 flex justify-end">
                  <button type="button" onClick={() => setShowAssetForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAssetSubmit} className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Vous pourrez compléter la fiche (prix, valorisation, photos…) ensuite.
                </p>
                <div>
                  <label className="label-uppercase block mb-1">Nom de l'actif</label>
                  <input value={assetFormData.name} onChange={e => setAssetFormData(p => ({...p, name: e.target.value}))} required
                    placeholder="Ex: Villa Saint-Tropez, Hôtel Bartoux..."
                    className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="asset-input-name" />
                </div>
                <div>
                  <label className="label-uppercase block mb-1">Adresse</label>
                  <input value={assetFormData.address} onChange={e => setAssetFormData(p => ({...p, address: e.target.value}))} required
                    placeholder="Rue, ville, code postal..."
                    className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="asset-input-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-uppercase block mb-1">Pôle</label>
                    <select value={assetFormData.pole} onChange={e => setAssetFormData(p => ({...p, pole: e.target.value}))}
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="asset-select-pole">
                      <option value="habitation">Habitation</option>
                      <option value="hotelerie">Hôtellerie</option>
                      <option value="commercial">Commercial</option>
                      <option value="promotion">Promotion</option>
                      <option value="bureaux">Bureaux</option>
                      <option value="plage_resto">Autres</option>
                      <option value="fonds_commerce">Fonds de commerce</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Statut</label>
                    <select value={assetFormData.status} onChange={e => setAssetFormData(p => ({...p, status: e.target.value}))}
                      className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="asset-select-status">
                      <option value="patrimonial">Patrimonial</option>
                      <option value="marchand_de_bien">Marchand de bien</option>
                      <option value="vendu">Vendu</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90"
                    data-testid="asset-submit-btn">Ajouter</button>
                  <button type="button" onClick={() => setShowAssetForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Detach asset confirmation */}
      {confirmDetachAssetId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDetachAssetId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="detach-asset-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Retirer cet actif de l'organigramme ?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              L'actif ne sera plus rattaché à sa société dans cet organigramme, mais restera disponible dans la page « Tous les actifs ».
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleDetachAsset(confirmDetachAssetId)}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-detach-asset-btn">Retirer</button>
              <button onClick={() => setConfirmDetachAssetId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Detach dossier confirmation */}
      {confirmDetachDossierId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDetachDossierId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="detach-dossier-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Retirer ce dossier de l'organigramme ?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Le dossier ne sera plus rattaché à sa société dans cet organigramme, mais restera disponible dans la page « Dossiers en cours ».
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleDetachDossier(confirmDetachDossierId)}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-detach-dossier-btn">Retirer</button>
              <button onClick={() => setConfirmDetachDossierId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Detach team member confirmation */}
      {confirmDetachTeamId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDetachTeamId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="detach-team-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Retirer ce membre de cette société ?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Le lien entre ce membre d'équipe et la société sera supprimé dans cet organigramme. Le membre restera disponible dans la page « Équipes ».
            </p>
            <div className="flex gap-3">
              <button onClick={async () => {
                const { memberId, companyId } = confirmDetachTeamId;
                const member = teams.find(t => t.id === memberId);
                if (member) {
                  const newCompanyIds = (member.company_ids || []).filter(c => c !== companyId);
                  try {
                    await api.put(`/teams/${memberId}`, { company_ids: newCompanyIds });
                    toast.success('Membre retiré');
                    setConfirmDetachTeamId(null);
                    fetchData();
                  } catch (err) { toast.error('Erreur'); }
                }
              }}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-detach-team-btn">Retirer</button>
              <button onClick={() => setConfirmDetachTeamId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
